import { api } from "./api";

const NOTIFICATIONS_UPDATED_EVENT = "wn:notifications-updated";

/**
 * Cross-component refresh signal. The NotificationBell listens for this and
 * refetches its unread count immediately, so mark-read / mark-all-read from
 * any page reflects on the bell badge without waiting for the 30s poll.
 */
export function emitNotificationsUpdated(detail = {}) {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(NOTIFICATIONS_UPDATED_EVENT, { detail }));
}

export function onNotificationsUpdated(handler) {
  if (typeof window === "undefined") return () => {};
  window.addEventListener(NOTIFICATIONS_UPDATED_EVENT, handler);
  return () => window.removeEventListener(NOTIFICATIONS_UPDATED_EVENT, handler);
}

export async function listNotifications(userId, { category, q, limit } = {}) {
  const params = { user_id: userId };
  if (category && category !== "all") params.category = category;
  if (q) params.q = q;
  if (limit) params.limit = limit;
  const { data } = await api.get("/notifications", { params });
  return data;
}

export async function unreadCount(userId) {
  const { data } = await api.get("/notifications/unread-count", {
    params: { user_id: userId },
  });
  return data.unread || 0;
}

export async function markRead(notifId, userId) {
  const { data } = await api.post(`/notifications/${notifId}/read`, {
    actor_user_id: userId,
  });
  emitNotificationsUpdated({ kind: "mark_one", id: notifId });
  return data;
}

export async function markAllRead(userId) {
  const { data } = await api.post(`/notifications/read-all`, {
    actor_user_id: userId,
  });
  emitNotificationsUpdated({ kind: "mark_all" });
  return data;
}

export async function listEmailLog(userId, { window, eventType, limit } = {}) {
  const params = { user_id: userId };
  if (window) params.window = window;
  if (eventType && eventType !== "all") params.event_type = eventType;
  if (limit) params.limit = limit;
  const { data } = await api.get("/admin/email-log", { params });
  return data;
}

export async function getEmailDetail(emailId, userId) {
  const { data } = await api.get(`/admin/email-log/${emailId}`, {
    params: { user_id: userId },
  });
  return data;
}

export const CATEGORIES = [
  { v: "all", label: "All" },
  { v: "unread", label: "Unread" },
  { v: "messages", label: "Messages" },
  { v: "approvals", label: "Approvals" },
  { v: "status", label: "Status" },
  { v: "documents", label: "Documents" },
  { v: "warnings", label: "Warnings" },
];

export function relativeTime(iso) {
  if (!iso) return "";
  let t;
  try {
    t = new Date(iso).getTime();
  } catch {
    return "";
  }
  const now = Date.now();
  const diff = Math.max(0, now - t);
  const m = Math.floor(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  const w = Math.floor(d / 7);
  if (w < 5) return `${w}w ago`;
  try {
    return new Date(iso).toISOString().slice(0, 10);
  } catch {
    return iso;
  }
}

export function formatTimestamp(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toISOString().replace("T", " ").slice(0, 19);
  } catch {
    return iso;
  }
}
